import type { ReactNode } from 'react';

/**
 * A placeholder the shape of the thing that is loading.
 *
 * Better than a spinner for a table: the page does not jump when the data
 * arrives, because the space was already the right size.
 */
export function Skeleton({
  width,
  height = 14,
  radius = 'sm',
}: {
  readonly width?: number | string;
  readonly height?: number;
  readonly radius?: 'sm' | 'md' | 'full';
}): ReactNode {
  return (
    <span
      className={`skeleton skeleton-${radius}`}
      style={{ width: width ?? '100%', height }}
      aria-hidden="true"
    />
  );
}

export function TableSkeleton({
  rows = 5,
  columns = 4,
}: {
  readonly rows?: number;
  readonly columns?: number;
}): ReactNode {
  return (
    <div className="table-skeleton" aria-hidden="true">
      {Array.from({ length: rows }, (_, row) => (
        <div key={row} className="table-skeleton-row">
          {Array.from({ length: columns }, (_, column) => (
            // Varied widths: a grid of identical bars reads as a rendering bug.
            <Skeleton key={column} width={column === 0 ? '40%' : '16%'} />
          ))}
        </div>
      ))}
    </div>
  );
}

/** Cards still loading, in the grid they will fill. */
export function CardGridSkeleton({ cards = 6 }: { readonly cards?: number }): ReactNode {
  return (
    <div className="card-grid card-grid-skeleton" aria-hidden="true">
      {Array.from({ length: cards }, (_, card) => (
        <span key={card} className="skeleton" />
      ))}
    </div>
  );
}
